/**
 * RegulationsTableToolbar Component
 * 法规表格工具栏主组件 - 只包含操作按钮
 */

import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  useGridApiContext,
} from "@mui/x-data-grid-premium";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import type { Dayjs } from "dayjs";
import {
  exportRegulationsToXlsx,
  type ConceptValue,
} from "../../../services/regulationsService";
import { useGlobalConfig } from "../../../hooks/useGlobalConfig";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import {
  useAppStore,
  selectSelectedRequirementIds,
} from "../../../stores";
import { ToolbarActions } from "./ToolbarActions";
import { BulkActions } from "./BulkActions";
import { RUN_AI_ASSESSMENT_BUTTON_CLASS } from "../RunAiAssessmentMenuButton";
import { useBrandThemeColors } from "../../../branding";

export interface RegulationsTableToolbarProps {
  totalRows: number;
  selectedCount?: number;
  onCompanyScopeImpact?: () => void;
  onCustomScopeImpact?: () => void;
  companyScopeStarting?: boolean;
  onBulkFavoritesSuccess?: (
    regulationIds: string[],
    isFavorite: boolean,
  ) => void;
  selectedMarkets: ConceptValue[];
  selectedProductTypes?: ConceptValue[];
  selectedHA?: ConceptValue[];
  selectedTags?: ConceptValue[];
  onMarketsChange: (markets: ConceptValue[]) => void;
  onProductTypesChange?: (items: ConceptValue[]) => void;
  onHAChange?: (items: ConceptValue[]) => void;
  onTagsChange?: (items: ConceptValue[]) => void;
  selectedTitle: string;
  onTitleChange: (title: string) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onDateRangeChange: (from: Dayjs | null, to: Dayjs | null) => void;
  /** When true, show the grid column filter panel toolbar button. */
  showGridFilterToolbarButton?: boolean;
  /** Badge count for column filters (only rows that have an effective filter value). */
  gridFilterToolbarBadgeCount?: number;
}

export const RegulationsTableToolbar: React.FC<RegulationsTableToolbarProps> = (
  props
) => {
  useGridApiContext();
  const { config } = useGlobalConfig();
  const maxRegulationsPerBatch = config?.max_regulations_per_batch ?? 5;
  const canExportRegulations = useHasPrivilege("gri.regulation.export");
  const selectedRegulationIds = useAppStore(selectSelectedRequirementIds);
  const [exporting, setExporting] = useState(false);
  const [exportSnackbarOpen, setExportSnackbarOpen] = useState(false);
  const [exportSnackbarMessage, setExportSnackbarMessage] = useState("");
  const [exportSnackbarSeverity, setExportSnackbarSeverity] = useState<
    "success" | "error"
  >("error");

  const {
    totalRows,
    selectedCount = 0,
    selectedMarkets,
    selectedProductTypes = [],
    selectedHA = [],
    selectedTags = [],
    onMarketsChange,
    onProductTypesChange,
    onHAChange,
    onTagsChange,
    selectedTitle,
    onTitleChange,
    fromDate,
    toDate,
    onDateRangeChange,
    onCompanyScopeImpact,
    onCustomScopeImpact,
    companyScopeStarting,
    onBulkFavoritesSuccess,
    showGridFilterToolbarButton = false,
    gridFilterToolbarBadgeCount = 0,
  } = props;

  const { primaryColor } = useBrandThemeColors();
  const gridToolbarFeatureButtonSlotProps = useMemo(
    () => ({
      button: {
        size: "small" as const,
        sx: {
          color: `${primaryColor} !important`,
          textTransform: "none",
          fontWeight: 500,
          "& .MuiSvgIcon-root": {
            color: `${primaryColor} !important`,
          },
          "& .MuiButton-startIcon .MuiSvgIcon-root": {
            color: `${primaryColor} !important`,
          },
          "&.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.26) !important",
            "& .MuiSvgIcon-root": {
              color: "rgba(0, 0, 0, 0.26) !important",
            },
            "& .MuiButton-startIcon .MuiSvgIcon-root": {
              color: "rgba(0, 0, 0, 0.26) !important",
            },
          },
        },
      },
    }),
    [primaryColor],
  );

  const gridToolbarFilterButtonSlotProps = useMemo(
    () => ({
      button: gridToolbarFeatureButtonSlotProps.button,
      badge: {
        badgeContent: gridFilterToolbarBadgeCount,
        sx: {
          "& .MuiBadge-badge": {
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "11px",
            lineHeight: "16px",
            minWidth: 18,
            height: 18,
            backgroundColor: primaryColor,
          },
        },
      },
    }),
    [
      gridToolbarFeatureButtonSlotProps.button,
      gridFilterToolbarBadgeCount,
      primaryColor,
    ],
  );

  const noExportSelection = selectedRegulationIds.length === 0;

  const handleToolbarExport = async () => {
    if (noExportSelection || exporting) return;
    setExporting(true);
    try {
      await exportRegulationsToXlsx(selectedRegulationIds);
      setExportSnackbarMessage("Export started. Check your downloads.");
      setExportSnackbarSeverity("success");
      setExportSnackbarOpen(true);
    } catch (err) {
      setExportSnackbarMessage(
        err instanceof Error ? err.message : "Export failed. Please try again.",
      );
      setExportSnackbarSeverity("error");
      setExportSnackbarOpen(true);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Box
        role="toolbar"
        data-testid="toolbar-container"
        sx={{
          p: "12px 16px",
          bgcolor: "background.paper",
          boxShadow: "none",
          border: "none",
          borderBottom: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left: Title and Count */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, color: "#262626", fontSize: "16px" }}
          >
            Regulations List
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#8c8c8c", fontSize: "14px" }}
          >
            {selectedCount > 0
              ? `${selectedCount}/${maxRegulationsPerBatch} Selected`
              : ""}
          </Typography>
        </Box>

        {/* Right: Actions and Built-in features */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            [`& .MuiButton-root:not(.${RUN_AI_ASSESSMENT_BUTTON_CLASS})`]: {
              color: "#595959 !important",
              textTransform: "none",
              fontWeight: 500,
            },
            // Only tint SVG icons — do not use "*" or Badge label inherits button grey with !important
            "& .MuiButton-startIcon .MuiSvgIcon-root": {
              color: "inherit !important",
            },
          }}
        >
          <BulkActions
            onCompanyScopeImpact={onCompanyScopeImpact}
            onCustomScopeImpact={onCustomScopeImpact}
            companyScopeStarting={companyScopeStarting}
            onBulkFavoritesSuccess={onBulkFavoritesSuccess}
          />
          <ToolbarActions
            selectedMarkets={selectedMarkets}
            selectedProductTypes={selectedProductTypes}
            selectedHA={selectedHA}
            selectedTags={selectedTags}
            selectedTitle={selectedTitle}
            fromDate={fromDate}
            toDate={toDate}
            onMarketsChange={onMarketsChange}
            onProductTypesChange={onProductTypesChange}
            onHAChange={onHAChange}
            onTagsChange={onTagsChange}
            onTitleChange={onTitleChange}
            onDateRangeChange={onDateRangeChange}
          />

          <GridToolbarDensitySelector slotProps={gridToolbarFeatureButtonSlotProps} />
          {showGridFilterToolbarButton ? (
            <GridToolbarFilterButton
              slotProps={gridToolbarFilterButtonSlotProps}
            />
          ) : null}
          <GridToolbarColumnsButton slotProps={gridToolbarFeatureButtonSlotProps} />
          {canExportRegulations ? (
            <Tooltip
              title={
                exporting
                  ? "Exporting…"
                  : noExportSelection
                    ? "Select regulations in the table before exporting."
                    : "Export selected regulations."
              }
              arrow
              placement="top"
            >
              <Box component="span" sx={{ display: "inline-flex" }}>
                <Button
                  size="small"
                  sx={gridToolbarFeatureButtonSlotProps.button.sx}
                  startIcon={
                    <FileDownloadOutlinedIcon sx={{ fontSize: "18px !important" }} />
                  }
                  disabled={exporting}
                  onClick={handleToolbarExport}
                >
                  {exporting ? "Exporting…" : "Export"}
                </Button>
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
      <Snackbar
        open={exportSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setExportSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={exportSnackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {exportSnackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
